/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import { Chip, IconButton, Stack } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import styled from 'styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { KeyboardArrowUp, MoreHoriz } from '@mui/icons-material';
import { TopAlignedCell } from './TopAlignedCell';

const BeamChip = styled(Chip)`
  border-radius: 4px;
  background: rgba(159, 175, 188, 0.1);
  padding: 6px 8px;

  & .MuiChip-label {
    color: #9fafbc;
    padding: unset;
    text-align: center;
    font-feature-settings: 'clig' off, 'liga' off;
    font-family: 'Source Sans Pro', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
  }
`;

const ChipStack = styled(Stack)`
  padding: 0;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
`;

interface ChipCellProps {
  params: GridRenderCellParams<any, string[], any, any>;
}

/**
 * This component displays a list of strings (a.k.a. tags) in a single row. It will automatically determine how many
 * tags can fit based on the parent cell's available space. If additional space is required to render all the tags,
 * the component will hide the additional tags, and display a button that allows the user to expand the cell to display
 * all the tags.
 *
 * The individual tags are rendered using the MUI `Chip` component.
 *
 * @warning When used in a data grid set `autoPageSize={false}`, otherwise the grid will jerk around when the user
 * expands or hides the tags.
 */
export const ChipCell = ({ params }: ChipCellProps) => {
  const chipLabels = params.value;
  const portalRef = useRef<HTMLDivElement>(null);
  const [visibleChips, setVisibleChips] = useState<string[] | undefined>(undefined);
  const [hiddenChips, setHiddenChips] = useState<string[] | undefined>(undefined);
  const [expanded, setExpanded] = useState(false);

  // This effect measures the width of all Chips that will be rendered by this component. It then uses the widths to
  // determine how many chips can fit into the width of the column.
  useEffect(() => {
    if (chipLabels === undefined) return;
    if (visibleChips !== undefined) return;

    const calculateVisibleChips = () => {
      if (portalRef.current) {
        const GAP_PX = 8;
        const CELL_PADDING_PX = 20;
        const EXPAND_ICON_WIDTH_PX = 34;
        const FUDGE_PX = 12;
        const AVAIL_CONTENT_WIDTH_PX = params.colDef.computedWidth - CELL_PADDING_PX - EXPAND_ICON_WIDTH_PX - FUDGE_PX;
        let totalWidth = 0;
        let chipsToDisplay: string[] = [];
        let chipsToHide: string[] = [];

        // For each rendered chip, sum up its width.
        portalRef.current.childNodes.forEach((node, index) => {
          const width = (node as HTMLElement).getBoundingClientRect().width + GAP_PX;
          if (totalWidth + width <= AVAIL_CONTENT_WIDTH_PX) {
            // If the value of totalWidth after adding the width of this chip is less than or equal to the available
            // column width, then this chip can be displayed.
            totalWidth += width;
            chipsToDisplay.push(chipLabels[index]);
          } else {
            // Otherwise, this chip will be hidden by default, and the user will need to click the expand button to
            // see the hidden chips.
            chipsToHide.push(chipLabels[index]);
          }
        });

        setVisibleChips(chipsToDisplay);
        setHiddenChips(chipsToHide.length > 0 ? chipsToHide : undefined);
      }
    };

    calculateVisibleChips();
  }, [chipLabels, params.colDef.computedWidth, visibleChips]);

  if (chipLabels === undefined) return <></>;

  const chipElements = visibleChips?.map(chipLabel => <BeamChip label={chipLabel} key={chipLabel} />);
  const hiddenChipElements = hiddenChips?.map(chipLabel => <BeamChip label={chipLabel} key={chipLabel} />);

  // This invisible portal is used to render the BeamChip so their widths can be determined.
  const portalContent = (
    <div ref={portalRef} style={{ opacity: 0 }}>
      {chipLabels.map(label => (
        <BeamChip label={label} key={label} />
      ))}
    </div>
  );

  let extraProps = {};
  if (!expanded) {
    // When not expanded, the max height is equal to the height of a chip element, and overflow is hidden.
    extraProps = {
      maxHeight: 32,
      overflow: 'hidden',
    };
  }
  return (
    <div>
      <ChipStack paddingTop={'8px'} paddingBottom={'8px'} {...extraProps}>
        {chipElements}
        {hiddenChips ? (
          <>
            {/* Show expand/close icon when there are hidden chip elements */}
            <IconButton
              size={'small'}
              onClick={() => {
                setExpanded(!expanded);
              }}
            >
              {!expanded ? <MoreHoriz /> : <KeyboardArrowUp />}
            </IconButton>

            {/* Display hidden chip elements when expanded. -2px top margin as flex-gap doesn't seem to take 1px borders
                into account when rendering the 8px gap between this and the former chip stack. Note that two separate
                chip stacks are needed to ensure no chips inadvertently sneak their way next to the always-shown chips. */}
            {expanded ? <ChipStack marginTop={'-2px'}>{hiddenChipElements}</ChipStack> : null}
          </>
        ) : (
          ''
        )}
      </ChipStack>
      {/* This portal is necessary in order for the chip overflow calculation to work. */}
      {typeof document !== 'undefined' && visibleChips === undefined && hiddenChips === undefined
        ? createPortal(portalContent, document.body)
        : null}
    </div>
  );
};

export const TopAlignedChipCell = ({ params }: { params: GridRenderCellParams<any, string[], any, any> }) => {
  return (
    <TopAlignedCell params={params}>
      <ChipCell params={params} />
    </TopAlignedCell>
  );
};
